import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LockClosedIcon, EyeIcon, EyeSlashIcon, UserIcon } from "@heroicons/react/24/outline";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useConsultantAuthStore from "../../store/consultant/useConsultantAuthStore";
import useAuthStore from "../../store/useAuthStore";

const ConsultantLogin = () => {
    const [credential, setCredential] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();
    const { login, isLoading, consultant } = useConsultantAuthStore();
    const authStore = useAuthStore();

    useEffect(() => {
        const storedToken = localStorage.getItem("consultantToken");
        if (storedToken && consultant) {
            // Also sync to global auth store if not already there
            if (!authStore.token || authStore.userType !== "consultant") {
                useAuthStore.setState({
                    user: consultant,
                    userType: "consultant",
                    token: storedToken
                });
            }
            navigate("/consultant/dashboard");
        }
    }, [navigate, consultant, authStore.token, authStore.userType]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCredential({ ...credential, [name]: value });
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setErrorMessage("");

        try {
            const success = await login(credential);
            if (success) {
                const updatedState = useConsultantAuthStore.getState();

                useAuthStore.setState({
                    user: updatedState.consultant,
                    userType: "consultant",
                    token: updatedState.token
                });

                navigate("/consultant/dashboard");
            } else {
                setErrorMessage("Invalid Email or Password");
            }
        } catch (err) {
            setErrorMessage("Invalid Email or Password");
        }
    };

    return (
        <div
            className="h-[100dvh] w-full flex items-center justify-center p-4 pb-20 relative overflow-hidden"
            style={{
                backgroundImage: "url('/assets/login.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <div className="flex flex-col lg:flex-row w-full max-w-4xl border border-gray-300 bg-white rounded-3xl shadow-2xl overflow-hidden relative z-10 mx-auto">
                <div className="flex flex-col justify-center w-full lg:w-2/3 p-8 lg:p-11">
                    <div className="text-start">
                        <img
                            src="/assets/black logo.png"
                            alt="Ducktail Logo"
                            className="h-10 lg:h-20 mb-3 lg:mb-6"
                        />
                        <p className="text-gray-600 mt-2 lg:mt-4 text-[10px] lg:text-base leading-tight">
                            Ducktail - The Construction Platform connects companies with
                            skilled professionals, offering reliable solutions for projects of
                            all sizes. From renovations to large developments, we ensure
                            quality and efficiency.
                        </p>
                    </div>
                </div>
                <div className="flex flex-col justify-center w-full lg:w-1/2 p-8 lg:p-10">
                    <h2 className="text-lg sm:text-2xl font-semibold text-center text-gray-800">
                        LOGIN (Consultant)
                    </h2>
                    <form className="mt-6" onSubmit={handleLogin}>
                        <div className="mb-2 lg:mb-4">
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pt-2">
                                    <UserIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type="email"
                                    name="email"
                                    value={credential.email}
                                    className="w-full pl-10 pr-3 py-1 lg:py-2 mt-1 lg:mt-2 border rounded-[1.5rem] bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-xs lg:text-base leading-8"
                                    placeholder="Email ID"
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="mb-1 lg:mb-2">
                            <div className="relative text-start">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pt-2">
                                    <LockClosedIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={credential.password}
                                    className="w-full pl-10 pr-3 py-1 lg:py-2 mt-1 lg:mt-2 border rounded-[1.5rem] bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-xs lg:text-base leading-8"
                                    style={{ backgroundColor: "#dddddd" }}
                                    placeholder="Password"
                                    onChange={handleInputChange}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 pt-2 text-gray-500 hover:text-gray-700"
                                >
                                    {showPassword ? (
                                        <EyeSlashIcon className="h-5 w-5" />
                                    ) : (
                                        <EyeIcon className="h-5 w-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col items-center pt-2">
                            {errorMessage && (
                                <p className="text-red-500 text-xs lg:text-sm font-semibold mb-2">
                                    {errorMessage}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`w-28 px-4 py-1 mt-4 text-black border-2 transition-all duration-300 flex items-center justify-center ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                                    }`}
                                style={{
                                    background: "linear-gradient(310deg, #dddddd 10%, white)",
                                    borderRadius: "24px",
                                }}
                            >
                                {isLoading ? (
                                    <AiOutlineLoading3Quarters className="animate-spin text-xl" />
                                ) : (
                                    "LOGIN"
                                )}
                            </button>
                        </div>

                        <div className="text-center mt-2">
                            <Link
                                to="/consultant/forgot-password"
                                className="text-xs text-blue-600 hover:underline"
                            >
                                Forgot Password?
                            </Link>
                        </div>

                        <p className="mt-2 lg:mt-4 text-center text-xs lg:text-sm text-gray-800">
                            Not registered yet?{" "}
                            <Link to="/consultant/signup" className="text-blue-600 hover:underline">
                                Sign up
                            </Link>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ConsultantLogin;
